import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageLayout } from '@/components';
import { Button, Input } from '@/components/ui';
import { Card } from '@/components/ui/Card';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { INDEXABLE_ROUTE_META } from '@/seo/routeMeta';

const COMMON_TAX_CODES = [
  { code: '1257L', description: 'Standard personal allowance' },
  { code: 'S1257L', description: 'Scottish standard allowance' },
  { code: 'C1257L', description: 'Welsh standard allowance' },
  { code: 'BR', description: 'Basic rate (20%) on all income' },
  { code: 'D0', description: 'Higher rate (40%) on all income' },
  { code: 'D1', description: 'Additional rate (45%) on all income' },
  { code: '0T', description: 'No personal allowance' },
  { code: 'K100', description: 'K code (benefits / underpaid tax)' },
];

export function TaxCodePage(): React.JSX.Element {
  const [taxCode, setTaxCode] = useState('');
  const navigate = useNavigate();

  useDocumentMeta(INDEXABLE_ROUTE_META['/tax-code']);

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (taxCode.trim()) {
      navigate(`/tax-code/${encodeURIComponent(taxCode.trim())}`);
    }
  };

  const handleQuickLink = (code: string): void => {
    navigate(`/tax-code/${encodeURIComponent(code)}`);
  };

  return (
    <PageLayout maxWidth="lg">
      <div className="space-y-8">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-3xl font-semibold text-gray-900">Tax Code Checker</h1>
          <p className="mt-2 text-gray-600">
            Enter your UK tax code to understand what it means for your tax situation
          </p>
        </div>

        {/* Input Form */}
        <Card padding="lg" className="max-w-md mx-auto">
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              id="tax-code"
              name="tax-code"
              type="text"
              value={taxCode}
              onChange={(e) => setTaxCode(e.target.value.toUpperCase())}
              label="Enter your tax code"
              placeholder="e.g., 1257L, S1257L, BR"
              fullWidth
              autoComplete="off"
              autoFocus
            />
            <Button
              type="submit"
              variant="primary"
              fullWidth
              disabled={!taxCode.trim()}
            >
              Check tax code
            </Button>
          </form>
        </Card>

        {/* Common Tax Codes */}
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-4 text-center">
            Common Tax Codes
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {COMMON_TAX_CODES.map(({ code, description }) => (
              <button
                key={code}
                onClick={() => handleQuickLink(code)}
                className="text-left p-4 bg-white border border-gray-200 rounded-lg hover:border-gray-400 hover:shadow-sm transition-all"
              >
                <span className="font-mono font-bold text-lg text-gray-900">{code}</span>
                <span className="block text-sm text-gray-500 mt-1">{description}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Info Section */}
        <Card padding="md" variant="outlined" className="bg-gray-50">
          <h3 className="font-semibold text-gray-900 mb-2">Where to find your tax code</h3>
          <ul className="text-sm text-gray-600 space-y-1">
            <li>On your payslip (usually near the top)</li>
            <li>On your P60 (end of year certificate)</li>
            <li>On your P45 (when leaving a job)</li>
            <li>On HMRC letters about your tax code</li>
            <li>In your HMRC Personal Tax Account online</li>
          </ul>
        </Card>
      </div>
    </PageLayout>
  );
}

export default TaxCodePage;
